import React, { useState } from 'react';
import { Mail, MapPin, Phone, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from '../hooks/use-toast';
import { photographerInfo } from '../data/mock';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Fehler beim Senden der Nachricht');
      }

      toast({
        title: "Nachricht gesendet! ✅",
        description: "Vielen Dank für deine Nachricht. Ich melde mich bald bei dir.",
      });
      
      setFormData({ name: '', email: '', subject: '', message: '' });
      
    } catch (error) {
      console.error('Error sending contact form:', error);
      toast({
        title: "Fehler beim Senden ❌",
        description: error.message || "Es gab ein Problem beim Senden deiner Nachricht. Bitte versuche es später erneut.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: Mail, label: 'E-Mail', value: photographerInfo.email },
    { icon: Phone, label: 'Telefon', value: photographerInfo.phone },
    { icon: MapPin, label: 'Standort', value: photographerInfo.location }
  ];

  return (
    <section id="contact" className="py-20 px-4 bg-gradient-to-b from-[#0a0a0a] to-black">
      <div className="max-w-6xl mx-auto">
        {/* Section Title */}
        <div className="mb-16 text-center">
          <h2
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            
          >
            Kontakt
          </h2>
          <div className="w-24 h-1 bg-amber-500 mx-auto mb-6" />
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Hast du Fragen oder möchtest mehr über meine Fotografie erfahren? Schreib mir gerne!
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-6" >
              Kontaktinformationen
            </h3>
            <p className="text-gray-400 mb-8">
              Du kannst mich gerne über diese Kanäle erreichen. Ich antworte normalerweise innerhalb von 24 Stunden.
            </p>
            <div className="space-y-6">
              {contactInfo.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-amber-500" />
                    </div>
                    <div>
                      <div className="text-gray-500 text-sm mb-1">{item.label}</div>
                      <div className="text-white font-medium">{item.value}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gray-900/50 p-8 rounded-lg border border-gray-800">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-gray-300 mb-2 text-sm font-medium">
                  Name
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="bg-gray-900 border-gray-700 text-white focus:border-amber-500 focus:ring-amber-500"
                  placeholder="Dein Name"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-gray-300 mb-2 text-sm font-medium">
                  E-Mail
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="bg-gray-900 border-gray-700 text-white focus:border-amber-500 focus:ring-amber-500"
                  placeholder="deine.email@beispiel.de"
                />
              </div>
              <div>
                <label htmlFor="subject" className="block text-gray-300 mb-2 text-sm font-medium">
                  Betreff
                </label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  className="bg-gray-900 border-gray-700 text-white focus:border-amber-500 focus:ring-amber-500"
                  placeholder="Worum geht es?"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-gray-300 mb-2 text-sm font-medium">
                  Nachricht
                </label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className="bg-gray-900 border-gray-700 text-white focus:border-amber-500 focus:ring-amber-500 resize-none"
                  placeholder="Erzähl mir mehr..."
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold py-6 rounded-lg transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  'Wird gesendet...'
                ) : (
                  <span className="flex items-center justify-center">
                    <Send className="w-5 h-5 mr-2" />
                    Nachricht senden
                  </span>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;